import type { Messages } from './messages';

/**
 * The Bokmål catalogue (ADR-031) — `en.ts` key for key, typed as `Messages` so a missing or
 * an extra key is a compile error. Norwegian typography throughout: «…» where English has “…”,
 * a spaced en dash " – " where English has " — ", sentence case, no trailing period on a
 * button, the informal "du". The glossary of the design spec is followed as it stands
 * (delplan, gjøremålsliste, oppgave, Skrivebeskyttet, Bytt lenke, Gi nytt navn, Laster …).
 *
 * Identical to English on purpose, and nothing else: the brand "Planlets" (`app.title`, the
 * `signIn.installLine` footer, whose identity string is never translated) and the arrows
 * `node.up` / `node.down`. The block-type ids become real words here (`gjøremålsliste`,
 * `oppgave`, `notat`); a type not listed falls back to its raw id like in every locale.
 */
export const nb: Messages = {
  resources: {
    snapshotReferencesHelp: "Et øyeblikksbilde krever at alle inkluderte referanser er offentlig tilgjengelige.",
    willPublish: "Gjøres offentlig",
    keptPrivate: "Holdes privat",
    loading: "Laster ressurser…",
    catalogue: "Min katalog",
    intro: "Presentasjoner, oppgaver og data, samlet på ett sted.",
    shared: "Delte ressurser",
    empty: "Ingen ressurser her ennå.",
    contents: "Innhold",
    name: "Ressursnavn",
    kind: "Ressurstype",
    create: "Legg til ressurs",
    example: "Prøv et bedriftseksempel",
    exampleHelp: "Oppretter en privat presentasjon med fiktive kvartalstall, forutsetninger og beregninger.",
    address: "Ressursens URN",
    open: "Åpne ressurs",
    copy: "Kopier adresse",
    copied: "Adressen er kopiert",
    copyFailed: "Marker og kopier adressen ovenfor.",
    back: "Tilbake til katalogen",
    parent: "Åpne overordnet ressurs",
    edit: "Rediger innhold",
    save: "Lagre innhold",
    cancel: "Avbryt",
    body: "Tekst",
    completed: "Fullført",
    events: "Kalenderhendelser",
    eventsHelp: "Én hendelse per linje: ÅÅÅÅ-MM-DD, tittel.",
    unit: "Enhet",
    frequency: "Periodeinndeling",
    data: "Tidsserie som CSV",
    dataHelp: "To kolonner: period,value. Bruk 2026-Q1, 2026-01, ÅÅÅÅ-MM-DD eller value for en skalar. Tom verdi betyr manglende data.",
    importCsv: "Importer CSV-fil",
    derived: "Beregn fra inndata",
    expression: "Formel",
    inputs: "Navngitte inndata",
    inputsHelp: "Én inndatakilde per linje: revenue = urn:uuid:…",
    formulaHelp: "Bruk +, −, *, /, parenteser, sum(series), forecast(history, growth, quarters) eller align(series, template). Beregninger kjøres på serveren.",
    reference: "URN til målressurs",
    follow: "Åpne referert ressurs",
    live: "Løpende",
    snapshot: "Øyeblikksbilde",
    calculated: "Sist beregnet",
    captured: "Tidspunkt for opptak",
    notCalculated: "Ikke beregnet ennå",
    unavailable: "Dette innholdet er utilgjengelig. Oppdater for å prøve igjen.",
    period: "Periode",
    value: "Verdi",
    missing: "Mangler",
    refresh: "Oppdater",
    publish: "Publiser",
    publishLive: "Publiser løpende",
    publishSnapshot: "Publiser øyeblikksbilde",
    liveHelp: "Den offentlige adressen beholdes. Åpne eller oppdater ressursen for å se de nyeste inndataverdiene.",
    snapshotHelp: "Opprett en egen offentlig ressurs med fast innhold og beregnede verdier. Den får sin egen adresse.",
    publicationBlocked: "Kan ikke publisere: en beregning kan ikke utføres eller bruker andres private data. Publisering av resultater fra andres private inndata er ikke tillatt ennå.",
    previewHelp: "Underressurser følger synlighetsinnstillingene sine. Referanser gir ikke tilgang til målressursene.",
    unavailableReferences: "Referanser offentlige seere ikke kan åpne",
    unpublish: "Avpubliser",
    permissions: "Tilgangsrettigheter",
    inheritance: "Underressurser arver tilgang med mindre den overstyres. Referanser beholder egne rettigheter.",
    move: "Flytt ressurs",
    destination: "URN til ny overordnet ressurs",
    delete: "Slett ressurs",
    deleteHelp: "Underressurser slettes også. Du kan angre fra katalogen din.",
    undo: "Angre sletting",
    deleted: "Ressurs slettet",
    badCsv: "Bruk nøyaktig to CSV-kolonner og endelige tall. Perioder må være unike.",
    badInputs: "Bruk et unikt navn = urn:uuid:… for hver linje.",
    badEvents: "Bruk en gyldig dato og tittel på hver kalenderrad.",
    fileFailed: "Filen kunne ikke leses. Lim inn innholdet i CSV-feltet.",
    snapshotImmutable: "Dette øyeblikksbildet har fast innhold. Synlighet og rettigheter kan fortsatt endres.",
    rename: "Gi ressursen nytt navn",
    unknownType: "Denne ressurstypen har ingen nettredigerer ennå.",
    types: {
      "catalogue": "Katalog",
      "presentation": "Presentasjon",
      "list": "Liste",
      "task": "Oppgave",
      "calendar": "Kalender",
      "time-series": "Tidsserie",
      "reference": "Referanse",
      "note": "Notat",
    } as Record<string, string>,
    frequencies: {
      quarterly: "Kvartalsvis",
      monthly: "Månedlig",
      daily: "Daglig",
      scalar: "Enkeltverdi",
    },
  },
  app: {
    title: 'Planlets',
    tagline: 'Komponerbare planleggingskomponenter – bygg planer av delbare blokker.',
    signedInWithoutEmail: 'Logget inn',
    signedIn: (email: string) => `Logget inn som ${email}`,
    signOut: 'Logg ut',
    dismiss: 'Lukk',
    sessionExpired: 'Økten din har gått ut – logg inn igjen.',
    sessionExpiredWith: (name: string) =>
      `Økten din har gått ut – logg inn igjen for å fortsette med «${name}».`,
    signInFailed: 'Innloggingen mislyktes. Prøv igjen.',
    deleteTitle: (name: string) => `Slette «${name}»?`,
    deleteMessage: 'Du kan angre dette fra startsiden.',
    delete: 'Slett',
    importSuffix: (name: string) => `${name} (min)`,
    newTaskTitle: 'Ny oppgave',
  },

  signIn: {
    heading: 'Logg inn',
    lead: 'Planlets bruker plattformkontoen din – velg hvordan du vil logge inn.',
    withProvider: (provider: string) => `Logg inn med ${provider}`,
    installLabel: 'installasjon',
    installLine: (install: string) => `Planlets ${install}`,
  },

  home: {
    deleted: (name: string) => `Slettet «${name}».`,
    undo: 'Angre',
    heading: 'Mine planer',
    loading: 'Laster planene dine …',
    empty: 'Ingen planer ennå – opprett den første nedenfor.',
    roleFor: (name: string) => `rolle for ${name}`,
    refresh: 'Oppdater',
  },

  openPlan: {
    createHeading: 'Opprett en plan',
    nameLabel: 'Plannavn',
    namePlaceholder: 'f.eks. Sommertur',
    create: 'Opprett',
    openHeading: 'Åpne en plan',
    idLabel: 'Plan-id',
    idPlaceholder: 'lim inn en plan-id',
    open: 'Åpne',
  },

  header: {
    planVersion: 'planversjon',
    yourAccess: 'tilgangen din',
    rename: 'Gi nytt navn',
    share: 'Del',
    move: 'Flytt',
    delete: 'Slett',
    makeItMine: 'Gjør den til min',
    close: 'Lukk',
    newPlanName: 'nytt plannavn',
    save: 'Lagre',
    cancel: 'Avbryt',
  },

  access: {
    owner: 'Eier',
    write: 'Kan redigere',
    read: 'Skrivebeskyttet',
    publicRead: 'Skrivebeskyttet (offentlig)',
    none: 'Ingen tilgang',
  },

  role: {
    owner: 'Eier',
    writer: 'Kan redigere',
    reader: 'Skrivebeskyttet',
    none: 'Ingen tilgang',
  },

  editor: {
    blockType: 'blokktype',
    blockTitle: 'blokktittel',
    titlePlaceholder: 'Hva planlegger du?',
    addBlock: 'Legg til blokk',
    emptyEditable: 'Ingen blokker ennå – legg til den første ovenfor.',
    emptyReadOnly: 'Ingen blokker ennå.',
  },

  blockType: {
    'todo-list': 'gjøremålsliste',
    task: 'oppgave',
    note: 'notat',
  },

  childPlans: {
    heading: 'Delplaner',
    loading: 'Laster delplaner …',
    empty: 'Ingen delplaner.',
    accessTo: (name: string) => `tilgang til ${name}`,
    nameLabel: 'navn på delplan',
    namePlaceholder: 'f.eks. Pakkeliste',
    add: 'Legg til delplan',
  },

  node: {
    up: '↑',
    down: '↓',
    renameButton: 'Gi nytt navn',
    addInsideButton: '+ Legg til inni',
    removeButton: 'Fjern',
    save: 'Lagre',
    cancel: 'Avbryt',
    moveUp: (title: string) => `flytt ${title} opp`,
    moveDown: (title: string) => `flytt ${title} ned`,
    rename: (title: string) => `gi ${title} nytt navn`,
    addInside: (title: string) => `legg til inni ${title}`,
    remove: (title: string) => `fjern ${title}`,
    newTitleFor: (title: string) => `ny tittel på ${title}`,
  },

  confirm: {
    cancel: 'Avbryt',
  },

  share: {
    heading: 'Del',
    headingFor: (name: string) => `Del «${name}»`,
    done: 'Ferdig',
    loading: 'Laster delingsinnstillinger …',
    visibility: 'Synlighet',
    publicMode: 'offentlig modus',
    mode: {
      inherit: 'Arv fra overordnet',
      private: 'Privat',
      public: 'Offentlig – alle med lenken',
    },
    linkLabel: 'Delingslenke',
    shareLink: 'delingslenke',
    rotateLink: 'Bytt lenke',
    people: 'Personer',
    nobodyElse: 'Ingen andre har tilgang.',
    roleFor: (email: string) => `rolle for ${email}`,
    grantRoles: {
      reader: 'Kan lese',
      writer: 'Kan redigere',
      none: 'Blokkert',
    },
    remove: (email: string) => `fjern ${email}`,
    removeButton: 'Fjern',
    grantEmail: 'e-post for tilgang',
    emailPlaceholder: 'navn@example.com',
    grantRole: 'rolle for tilgang',
    add: 'Legg til',
  },

  move: {
    heading: (name: string) => `Flytt «${name}»`,
    loading: 'Laster plasseringer …',
    currentLocation: 'nåværende plassering',
    atTopLevel: 'På øverste nivå.',
    under: (name: string) => `Under «${name}».`,
    noOtherPlans: 'Du eier ingen andre planer å flytte denne inn i.',
    moveTo: 'Flytt til',
    destination: 'ny plassering',
    topLevel: 'Øverste nivå (ingen overordnet)',
    move: 'Flytt',
    cancel: 'Avbryt',
  },

  sharedViewer: {
    sharedView: 'delt visning',
    backToRoot: 'Tilbake til den delte planen',
    loading: 'Laster …',
    planVersion: 'planversjon',
    empty: 'Ingenting her ennå.',
    subPlanlets: 'Delplaner',
    noPublicSubPlanlets: 'Ingen offentlige delplaner.',
  },

  todo: {
    addTask: 'Legg til oppgave',
    newTask: 'ny oppgave',
    addPlaceholder: 'Legg til en oppgave',
    empty: 'Ingen oppgaver ennå.',
    save: 'Lagre',
    cancel: 'Avbryt',
    list: (title: string) => `gjøremålsliste ${title}`,
    listMoveUp: (title: string) => `flytt listen ${title} opp`,
    listMoveDown: (title: string) => `flytt listen ${title} ned`,
    listRename: (title: string) => `gi listen ${title} nytt navn`,
    listNewTitle: (title: string) => `ny tittel på listen ${title}`,
    listRemove: (title: string) => `fjern listen ${title}`,
    done: (title: string) => `${title} fullført`,
    rename: (title: string) => `gi ${title} nytt navn`,
    newTitle: (title: string) => `ny tittel på ${title}`,
    moveUp: (title: string) => `flytt ${title} opp`,
    moveDown: (title: string) => `flytt ${title} ned`,
    remove: (title: string) => `fjern ${title}`,
  },

  errors: {
    unreachable: 'Tjenesten er ikke tilgjengelig. Prøv igjen.',
    maxNesting: (description: string) => `Maksimalt antall nivåer nådd – ${description}`,
  },

  auth: {
    parFailed: (status: string) => `Innloggingen kunne ikke starte (PAR ${status}).`,
    noneInProgress: 'Ingen innlogging pågår – start på nytt.',
    stateMismatch: 'Innloggingssvaret hører ikke til denne nettleserøkten.',
    tokenFailed: (status: string) => `Innloggingen kunne ikke fullføres (token ${status}).`,
  },

  unsupported: {
    'insecure-context': 'Denne WebView-en kjører ikke appen i en sikker kontekst.',
    'no-webcrypto': 'Denne WebView-en mangler WebCrypto – oppdater Android System WebView.',
    'no-random-uuid':
      'Denne WebView-en er for gammel (mangler crypto.randomUUID) – ' +
      'oppdater Android System WebView.',
  },

  language: {
    label: 'Språk',
  },

  /** The Grove navigation, appearance settings, and supporting interface copy. */
  grove: {
    headerNote: 'Rom for å komme videre.',
    appearance: 'Utseende',
    system: 'Følg enheten',
    light: 'Lyst',
    dark: 'Mørkt',
    workspace: 'Arbeidsområde',
    plans: 'Planer',
    shared: 'Delt',
    settings: 'Innstillinger',
    sidebarNote: 'Ett sted. Mange muligheter.',
    skipContent: 'Hopp til innholdet',
    settingsEyebrow: 'Gjør deg hjemme',
    settingsDescription: 'Noen valg som gjør dette til ditt eget sted.',
    appearanceDescription: 'Velg et utseende, eller følg enheten. Valget ditt lagres bare på denne enheten.',
    colorMode: 'Fargemodus',
    account: 'Kontoen din',
    accountDescription: 'Bruk den samme kontoen for å fortsette med planene dine på nett, iOS og Android.',
    planActions: 'Planhandlinger',
    actionsFor: (title: string) => `Handlinger for ${title}`,
    addTaskTo: (title: string) => `Legg til oppgave i ${title}`,
    subplanDescription: 'Hold relaterte planer samlet.',
    subplanPlaceholder: 'Gi delplanen din et navn',
    progressTitle: 'Framdrift i planen',
    progressSummary: (completed: number, total: number) => `${completed} av ${total} oppgaver fullført`,
    completedTasks: 'Fullførte oppgaver',
    remainingTasks: (remaining: number) => `Gjenstår: ${remaining}`,
    listComplete: (completed: number, total: number) => `${completed} / ${total} fullført`,
    homeEyebrow: 'Ditt sted',
    homeDescription: 'Et oversiktlig sted for prosjektene og hverdagsplanene dine.',
    sharedEyebrow: 'Sammen om planene',
    sharedHeading: 'Delt med meg',
    sharedDescription: 'Planene andre har invitert deg til å lese eller jobbe med.',
    sharedLoading: 'Laster delte planer …',
    sharedEmpty: 'Ingen delte planer ennå. Planer som deles med kontoen din, vises her.',
    createDescription: 'Gi det neste prosjektet eller den private planen din et sted å starte.',
    openDescription: 'Har du allerede en plan-id? Fortsett der du slapp.',
    namePlaceholder: 'f.eks. Produktlansering eller sommertur',
    signinEyebrow: 'Litt struktur. Flere muligheter.',
    signinHeadingStart: 'Gi plass til',
    signinHeadingEnd: 'det som kommer.',
    signinDescription: 'Samle prosjekter, felles oppgaver og private planer på ett oversiktlig sted.',
    planPrinciple: 'Start med en plan.',
    planPrincipleDescription: 'Del den opp i overkommelige deler.',
    togetherPrinciple: 'Bygg den sammen.',
    togetherPrincipleDescription: 'Gi de rette personene tilgang.',
    movePrinciple: 'Kom videre.',
    movePrincipleDescription: 'Ett skritt om gangen.',
    welcome: 'Velkommen til Planlets',
    signinAccount: 'Velg en konto for å fortsette til planene dine.',
    accountNote: 'Bruk den samme kontoen på nett, iOS og Android.',
    collaborate: 'Samarbeid',
    sharingDescription: 'Velg hvordan planen deles, og hva hver person kan gjøre.',
    emailAddress: 'E-postadresse',
    permission: 'Tilgang',
    organize: 'Organiser',
    sharedNavigation: 'Navigasjon i delt plan',
    sharedReadOnly: 'Delt visning · Skrivebeskyttet',
    sharedPlan: 'Delt plan',
  },
};
